import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { jwtDecode } from "jwt-decode";
import {
  ImagePlus,
  Send,
  X,
  MoreHorizontal,
  Pencil,
  Trash2,
  Reply,
  ArrowDown,
  SmilePlus,
  Hash,
  Users,
  Lock,
  Settings,
  ChevronLeft,
  Paperclip,
  FileText,
} from "lucide-react";
import {
  MEDIA_IMAGE_MAX_BYTES,
  MEDIA_VIDEO_MAX_BYTES,
  MEDIA_MESSAGE_MAX_FILES,
  isImageFile,
  isVideoFile,
} from "@/lib/parseVideoEmbed";
import {
  isAllowedTeamFileUpload,
} from "@/lib/teamFileUpload";
import { useAuth } from "../../scripts/AuthContext";
import { useDmSocket } from "../../scripts/DmSocketContext";
import { useDmThread } from "../../hooks/useDmThread";
import { findSeenReceiptMessageId } from "@/lib/dmSeenReceipt";
import { getFullName } from "@/lib/userUtils";
import { toastError, toastSuccess } from "@/lib/toast";
import {
  listChannelSlashCommands,
  postCircatronInteractionCallback,
} from "@/lib/dmApi";
import { setLocalDmViewing } from "@/lib/dmViewingSync";
import SlashCommandSuggestionList from "@/components/dm/SlashCommandSuggestionList";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import MemberProfileHover from "@/components/MemberProfileHover";
import InviteAndMentionDialog from "@/components/InviteAndMentionDialog";
import DmMessageContextMenu, {
  DmMessageContextTarget,
} from "@/components/dm/DmMessageContextMenu";
import {
  extractPostPermalinkFromText,
  stripLonePostPermalink,
} from "@/lib/permalinks";
import {
  clearDmDraft,
  loadDmDraft,
  saveDmDraft,
  DM_QUICK_REACT_EMOJIS,
  addDmGroupMembers,
  removeDmGroupMember,
  updateDmGroup,
  joinChannel,
  inviteToChannel,
  removeChannelMember,
  createDmGroup,
  updateChannel,
  getChannel,
} from "@/lib/dmApi";
import {
  collectReferencesFromMarkdown,
  userReferenceMarkdown,
} from "@/lib/contentReferences";
import { resolvePresence } from "@/lib/presence";
import { plainExcerpt, markdownToPlain } from "@/lib/markdownPlain";
import MarkdownBody from "../MarkdownBody";
import EmojiInsertButton from "../EmojiInsertButton";
import RichMessageEditor, {
  isMarkdownEmpty,
  normalizeEditorMarkdown,
} from "./RichMessageEditor";
import DmPostAttachmentCard from "./DmPostAttachmentCard";
import DmGifAttachment from "./DmGifAttachment";
import DmFileAttachments from "./DmFileAttachments";
import CircatronEmbedCard from "./CircatronEmbedCard";
import DmAttachPickerDialog from "./DmAttachPickerDialog";
import GifPickerButton from "./GifPickerButton";
import MobileMessageComposer from "./MobileMessageComposer";
import DmMessageReactions from "./DmMessageReactions";
import ChannelPostingPolicyFields from "./ChannelPostingPolicyFields";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";
import { pinChatScrollToBottom } from "@/lib/pinChatScrollToBottom";
import {
  formatDmTimestamp,
  getMessageClusterMeta,
} from "@/lib/dmMessageCluster";

const SYSTEM_SENDER = "system";
const NEAR_BOTTOM_PX = 80;

function hasOsFiles(dt) {
  if (!dt) return false;
  if (dt.files && dt.files.length > 0) return true;
  return Array.from(dt.types || []).includes("Files");
}

function PendingLocalChip({ file, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!isImageFile(file)) return undefined;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs max-w-full">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          className="h-10 w-10 rounded object-cover shrink-0"
        />
      ) : isImageFile(file) ? (
        <ImagePlus className="h-3.5 w-3.5 shrink-0" />
      ) : (
        <FileText className="h-3.5 w-3.5 shrink-0" />
      )}
      <span className="truncate flex-1">{file.name}</span>
      <button
        type="button"
        className="p-0.5 hover:opacity-70"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/**
 * Shared DM thread: message list + markdown composer + emoji + files/GIF.
 */
export default function DmThread({
  orgId,
  conversationId,
  peer,
  isSelf = false,
  conversation = null,
  onConversationChange,
  headerExtra,
  className,
  compact = false,
  showPeerHeader = true,
  focusMessageId = null,
  onBack,
}) {
  const { accessToken } = useAuth();
  const isMobile = useIsMobile();
  const useMobileComposer = compact || isMobile;
  const { presenceByUserId, subscribe, emitTyping } = useDmSocket();
  const currentUserId = useMemo(() => {
    if (!accessToken) return null;
    try {
      const payload = jwtDecode(accessToken);
      return payload?.sub || null;
    } catch {
      return null;
    }
  }, [accessToken]);

  const channelLockedHint =
    conversation?.type === "channel" &&
    conversation?.is_member === false &&
    conversation?.visibility !== "public" &&
    conversation?.can_preview !== true;

  const {
    messages,
    loading,
    sending,
    send,
    sendMedia,
    editMessage,
    removeMessage,
    reactToMessage,
    preview,
    accessDenied,
    refresh,
    loadOlder,
    peerLastReadAt,
  } = useDmThread(orgId, conversationId, {
    skipMessages: channelLockedHint,
  });

  // Cross-tab: claim this conversation while the thread is visible so other
  // tabs suppress OS notifications / toasts / duplicate sound.
  useEffect(() => {
    const canClaim =
      !!orgId &&
      !!conversationId &&
      !channelLockedHint &&
      !accessDenied;
    const sync = () => {
      if (canClaim && typeof document !== "undefined" && !document.hidden) {
        setLocalDmViewing(orgId, conversationId);
      } else {
        setLocalDmViewing(null, null);
      }
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      setLocalDmViewing(null, null);
    };
  }, [orgId, conversationId, channelLockedHint, accessDenied]);

  const seenReceiptMessageId = useMemo(
    () =>
      findSeenReceiptMessageId(messages, currentUserId, peerLastReadAt),
    [messages, currentUserId, peerLastReadAt]
  );
  const [draft, setDraft] = useState(() =>
    orgId && conversationId ? loadDmDraft(orgId, conversationId) : ""
  );
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [pendingLocalFiles, setPendingLocalFiles] = useState([]);
  const [pendingTeamFiles, setPendingTeamFiles] = useState([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const dropDepthRef = useRef(0);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [messageMenu, setMessageMenu] = useState(null);
  const [canManageIntegrations, setCanManageIntegrations] = useState(false);
  const [stickToBottom, setStickToBottom] = useState(true);
  const [showJump, setShowJump] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [typingUsers, setTypingUsers] = useState(() => new Map());
  const [membersOpen, setMembersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [channelMeta, setChannelMeta] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [joining, setJoining] = useState(false);
  const [invitePending, setInvitePending] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const listRef = useRef(null);
  const listContentRef = useRef(null);
  const composerRef = useRef(null);
  const editEditorRef = useRef(null);
  const titleInputRef = useRef(null);
  const prevLenRef = useRef(0);
  const prevLastMessageIdRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  // Locks stick-to-bottom / onScroll while we intentionally navigate to a reply target
  const navigatingToMessageRef = useRef(false);
  const highlightTimerRef = useRef(null);
  const typingIdleRef = useRef(null);
  const typingActiveRef = useRef(false);
  const lastTypingTrueAtRef = useRef(0);
  const emitTypingRef = useRef(emitTyping);
  emitTypingRef.current = emitTyping;
  const conversationDraftKey = `${orgId ?? ""}:${conversationId ?? ""}`;
  const draftKeyRef = useRef(conversationDraftKey);
  // Keep draft in sync with the active conversation before paint (editor remounts via key)
  if (draftKeyRef.current !== conversationDraftKey) {
    draftKeyRef.current = conversationDraftKey;
    setDraft(
      orgId && conversationId ? loadDmDraft(orgId, conversationId) : ""
    );
  }

  const isGroup = conversation?.type === "group";
  const isChannel = conversation?.type === "channel";
  const isPersonalBotDm = useMemo(() => {
    if (isSelf || isGroup || isChannel) return false;
    if (peer?.is_bot) return true;
    const uid = String(peer?.user_id || "");
    return uid.startsWith("cbot_");
  }, [isSelf, isGroup, isChannel, peer?.is_bot, peer?.user_id]);
  const slashEnabled = isChannel || isPersonalBotDm;
  const channelTeamId = isChannel
    ? Number(conversation?.team_id)
    : null;

  const [slashCommands, setSlashCommands] = useState([]);
  const [slashLoading, setSlashLoading] = useState(false);
  const [slashSelectedIndex, setSlashSelectedIndex] = useState(0);
  const [slashMenuDismissed, setSlashMenuDismissed] = useState(false);
  const composerWrapRef = useRef(null);

  useEffect(() => {
    if (!slashEnabled || !accessToken || !orgId || !conversationId) {
      setSlashCommands([]);
      return undefined;
    }
    let cancelled = false;
    setSlashLoading(true);
    listChannelSlashCommands(accessToken, orgId, conversationId)
      .then((rows) => {
        if (!cancelled) setSlashCommands(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setSlashCommands([]);
      })
      .finally(() => {
        if (!cancelled) setSlashLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slashEnabled, accessToken, orgId, conversationId]);

  useEffect(() => {
    setSlashMenuDismissed(false);
    setSlashSelectedIndex(0);
  }, [draft, conversationId]);

  const slashQueryMatch = useMemo(() => {
    if (!slashEnabled || slashMenuDismissed) return null;
    const text = String(draft || "");
    // Leading / only; hide once the user types a space (command chosen).
    const m = text.match(/^\/([^\s]*)$/);
    return m ? m[1].toLowerCase() : null;
  }, [draft, slashEnabled, slashMenuDismissed]);

  const filteredSlashCommands = useMemo(() => {
    if (slashQueryMatch == null) return [];
    const q = slashQueryMatch;
    return slashCommands.filter(
      (c) => !q || String(c.command || "").toLowerCase().startsWith(q)
    );
  }, [slashCommands, slashQueryMatch]);

  const slashMenuOpen =
    slashQueryMatch != null &&
    (slashLoading || filteredSlashCommands.length > 0 || slashCommands.length === 0);

  useEffect(() => {
    if (slashSelectedIndex >= filteredSlashCommands.length) {
      setSlashSelectedIndex(0);
    }
  }, [filteredSlashCommands.length, slashSelectedIndex]);

  const applySlashCommand = useCallback(
    (cmd) => {
      if (!cmd?.command) return;
      const next = `/${cmd.command} `;
      setDraft(next);
      if (orgId && conversationId) saveDmDraft(orgId, conversationId, next);
      composerRef.current?.setMarkdown?.(next);
      setSlashMenuDismissed(true);
      requestAnimationFrame(() => composerRef.current?.focus?.("end"));
    },
    [orgId, conversationId]
  );

  const onSlashEditorKeyDown = useCallback(
    (event) => {
      if (!slashMenuOpen || !filteredSlashCommands.length) {
        if (slashMenuOpen && event.key === "Escape") {
          setSlashMenuDismissed(true);
          return true;
        }
        return false;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSlashSelectedIndex(
          (i) => (i + 1) % filteredSlashCommands.length
        );
        return true;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setSlashSelectedIndex(
          (i) =>
            (i - 1 + filteredSlashCommands.length) %
            filteredSlashCommands.length
        );
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        applySlashCommand(filteredSlashCommands[slashSelectedIndex]);
        return true;
      }
      if (event.key === "Escape") {
        setSlashMenuDismissed(true);
        return true;
      }
      return false;
    },
    [
      slashMenuOpen,
      filteredSlashCommands,
      slashSelectedIndex,
      applySlashCommand,
    ]
  );

  const pendingFileCount = pendingLocalFiles.length + pendingTeamFiles.length;
  const remainingAttach = Math.max(0, MEDIA_MESSAGE_MAX_FILES - pendingFileCount);

  const addLocalFiles = useCallback(
    (incoming) => {
      const list = Array.from(incoming || []).filter(Boolean);
      if (!list.length) return;
      const accepted = [];
      for (const file of list) {
        if (!isAllowedTeamFileUpload(file.name, file.type)) {
          toastError(`File type not allowed: ${file.name}`);
          continue;
        }
        const tooBig = isVideoFile(file)
          ? file.size > MEDIA_VIDEO_MAX_BYTES
          : file.size > MEDIA_IMAGE_MAX_BYTES;
        if (tooBig) {
          toastError(
            `${file.name} exceeds the ${isVideoFile(file) ? "100MB" : "10MB"} limit`
          );
          continue;
        }
        accepted.push(file);
      }
      if (!accepted.length) return;
      setPendingAttachment(null);
      setPendingLocalFiles((prev) => {
        const room = MEDIA_MESSAGE_MAX_FILES - prev.length - pendingTeamFiles.length;
        if (room <= 0) {
          toastError(`You can attach up to ${MEDIA_MESSAGE_MAX_FILES} files`);
          return prev;
        }
        if (accepted.length > room) {
          toastError(`You can attach up to ${MEDIA_MESSAGE_MAX_FILES} files`);
        }
        return [...prev, ...accepted.slice(0, room)];
      });
    },
    [pendingTeamFiles.length]
  );

  const addTeamFiles = useCallback((refs) => {
    const list = Array.isArray(refs) ? refs : [];
    if (!list.length) return;
    setPendingAttachment(null);
    setPendingTeamFiles((prev) => {
      const seen = new Set(prev.map((t) => `${t.team_id}:${t.node_id}`));
      const next = [...prev];
      for (const ref of list) {
        const key = `${ref.team_id}:${ref.node_id}`;
        if (seen.has(key)) continue;
        if (next.length + pendingLocalFiles.length >= MEDIA_MESSAGE_MAX_FILES) break;
        seen.add(key);
        next.push(ref);
      }
      return next;
    });
  }, [pendingLocalFiles.length]);
  const isDirect =
    !isSelf && !isGroup && !isChannel && Boolean(peer?.user_id);
  const groupTitle = conversation?.title || (isChannel ? "Channel" : "Group");
  const members = useMemo(
    () => conversation?.members || [],
    [conversation?.members]
  );
  const isOwner = !!conversation?.is_owner;
  const channelPublic = isChannel && conversation?.visibility === "public";
  const channelPublicOnPrivateTeam =
    channelPublic && conversation?.team_visibility === "restricted";
  const canManageChannel =
    isChannel &&
    !!(conversation?.can_manage ?? channelMeta?.can_manage);
  const canRenameChannel =
    canManageChannel &&
    !(conversation?.is_default ?? channelMeta?.is_default);
  const canEditTitle = isGroup || canRenameChannel;
  const isLocked = Boolean(channelLockedHint || accessDenied);
  const isPreview =
    !isLocked &&
    (!!preview ||
      (isChannel && conversation?.is_member === false && channelPublic));

  useEffect(() => {
    setChannelMeta(null);
    setPendingLocalFiles([]);
    setPendingTeamFiles([]);
    setPendingAttachment(null);
    setAttachOpen(false);
    dropDepthRef.current = 0;
    setDropActive(false);
  }, [conversationId]);

  useEffect(() => {
    if (
      !isChannel ||
      !accessToken ||
      !orgId ||
      !conversationId ||
      (conversation?.can_manage != null && conversation?.can_send != null)
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ch = await getChannel(accessToken, orgId, conversationId);
        if (cancelled || !ch) return;
        setChannelMeta({
          can_manage: ch.can_manage,
          can_send: ch.can_send,
          posting_policy: ch.posting_policy,
          is_default: ch.is_default,
          allowed_poster_ids: ch.allowed_poster_ids,
          allowed_posters: ch.allowed_posters,
        });
        onConversationChange?.({
          ...conversation,
          ...ch,
          can_manage: ch.can_manage,
          can_send: ch.can_send,
          posting_policy: ch.posting_policy,
          is_default: ch.is_default,
          allowed_poster_ids: ch.allowed_poster_ids,
          allowed_posters: ch.allowed_posters,
        });
      } catch {
        /* ignore — rename stays unavailable */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChannel, accessToken, orgId, conversationId]);

  const membersById = useMemo(() => {
    const map = {};
    if (peer?.user_id) map[peer.user_id] = peer;
    for (const m of members) {
      const u = m.user || m;
      const id = u?.user_id || u?.userId || m.user_id;
      if (id) map[id] = { ...u, user_id: id, title: m.title || u.title };
    }
    return map;
  }, [peer, members]);

  const handleInviteAndMention = useCallback((item, helpers) => {
    if (!item?.canInvite) return;
    if (
      isChannel &&
      (isLocked || isPreview || conversation?.is_member === false)
    ) {
      return;
    }
    setInviteError(null);
    setInvitePending({
      item,
      helpers,
      mode: isDirect ? "create-group" : "invite",
    });
  }, [
    isChannel,
    isDirect,
    isLocked,
    isPreview,
    conversation?.is_member,
  ]);

  const referenceContext = useMemo(
    () =>
      orgId &&
      accessToken &&
      !isPreview &&
      !isLocked &&
      !isSelf
        ? {
            enabled: true,
            orgId,
            accessToken,
            conversationId,
            contextType: isChannel ? "channel" : isGroup ? "group" : "dm",
            onInviteAndMention: handleInviteAndMention,
          }
        : null,
    [
      orgId,
      accessToken,
      conversationId,
      isPreview,
      isLocked,
      isChannel,
      isGroup,
      isSelf,
      handleInviteAndMention,
    ]
  );

  const confirmInviteAndMention = async () => {
    if (!invitePending || !accessToken || !orgId) return;
    const { item, helpers, mode } = invitePending;
    setInviteLoading(true);
    setInviteError(null);
    try {
      if (mode === "create-group") {
        const peerId = peer?.user_id;
        if (!peerId) throw new Error("Missing DM peer");

        // @query was already removed when the dialog opened; read live markdown.
        let baseDraft = normalizeEditorMarkdown(
          helpers.editor?.getMarkdown?.() ?? draft ?? ""
        );
        baseDraft = baseDraft
          .replace(/(^|\s)@[^\s[\]]*(\s*)$/u, "$1$2")
          .replace(/[ \t]+$/g, "")
          .trimEnd();

        const mention = userReferenceMarkdown(
          orgId,
          item.targetId,
          item.displayName
        );
        const nextDraft = `${[baseDraft, mention]
          .filter(Boolean)
          .join(baseDraft && !/\s$/.test(baseDraft) ? " " : "")} `;

        const conv = await createDmGroup(accessToken, orgId, {
          memberUserIds: [peerId, item.targetId],
        });
        const newId = conv.conversation_id || conv.conversationId;
        saveDmDraft(orgId, newId, nextDraft);
        // Clear the source 1:1 draft so an empty chat can be purged on leave.
        clearDmDraft(orgId, conversationId);
        setDraft("");
        composerRef.current?.clear?.();
        setInvitePending(null);
        onConversationChange?.({
          ...conv,
          conversation_id: newId,
          navigate: true,
          purge_conversation_id: conversationId,
        });
        return;
      }

      if (isChannel) {
        if (isLocked || isPreview || conversation?.is_member === false) {
          throw new Error("You must be a channel member to invite others");
        }
        await inviteToChannel(accessToken, orgId, conversationId, item.targetId);
      } else if (isGroup) {
        await addDmGroupMembers(accessToken, orgId, conversationId, [
          item.targetId,
        ]);
      } else {
        throw new Error("Cannot invite in this conversation");
      }
      helpers.insertMention?.();
      setInvitePending(null);
      onConversationChange?.();
    } catch (err) {
      console.error("Invite and mention failed:", err);
      setInviteError(
        err.response?.data?.message ||
          err.message ||
          "Could not invite this person"
      );
    } finally {
      setInviteLoading(false);
    }
  };

  useEffect(() => {
    setPendingAttachment(null);
    setPendingLocalFiles([]);
    setPendingTeamFiles([]);
    setAttachOpen(false);
    setEditingId(null);
    setEditDraft("");
    setReplyTo(null);
    setStickToBottom(true);
    stickToBottomRef.current = true;
    navigatingToMessageRef.current = false;
    setShowJump(false);
    setHighlightedMessageId(null);
    setTypingUsers(new Map());
    setEditingTitle(false);
    setTitleDraft("");
    prevLenRef.current = 0;
    prevLastMessageIdRef.current = null;
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    if (typingIdleRef.current) clearTimeout(typingIdleRef.current);
    typingIdleRef.current = null;
    if (typingActiveRef.current) {
      typingActiveRef.current = false;
      lastTypingTrueAtRef.current = 0;
      emitTypingRef.current?.(conversationId, false);
    }
  }, [conversationId, orgId]);

  useEffect(() => {
    if (!orgId || !conversationId) return;
    saveDmDraft(orgId, conversationId, draft);
  }, [draft, orgId, conversationId]);

  useEffect(() => {
    if (!accessToken || !orgId) {
      setCanManageIntegrations(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const meRes = await axios.get(`${API_BASE_URL}/org/${orgId}/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (cancelled) return;
        const caps = new Set(
          Array.isArray(meRes.data?.capabilities)
            ? meRes.data.capabilities
            : []
        );
        setCanManageIntegrations(
          Boolean(meRes.data?.is_owner) || caps.has("org.integrations")
        );
      } catch {
        if (!cancelled) setCanManageIntegrations(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId]);

  useEffect(() => {
    return subscribe((event, payload) => {
      if (String(payload?.org_id) !== String(orgId)) return;
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (event === "dm.typing") {
        const uid = payload?.user_id;
        if (!uid || uid === currentUserId) return;
        setTypingUsers((prev) => {
          const next = new Map(prev);
          if (payload.typing) {
            // Keep expiry ahead of sender idle (2.5s) + network slack
            next.set(uid, Date.now() + 4000);
          } else {
            next.delete(uid);
          }
          return next;
        });
        return;
      }
      if (event === "dm.message" && payload?.message?.sender_id) {
        setTypingUsers((prev) => {
          if (!prev.has(payload.message.sender_id)) return prev;
          const next = new Map(prev);
          next.delete(payload.message.sender_id);
          return next;
        });
      }
    });
  }, [subscribe, orgId, conversationId, currentUserId]);

  useEffect(() => {
    const t = setInterval(() => {
      setTypingUsers((prev) => {
        if (prev.size === 0) return prev;
        const now = Date.now();
        let changed = false;
        const next = new Map();
        for (const [uid, exp] of prev) {
          if (exp > now) next.set(uid, exp);
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const signalTyping = () => {
    if (!conversationId || isSelf) return;
    const now = Date.now();
    // Emit on start, and heartbeat while still typing so peers refresh and
    // a second typing session always re-triggers after idle/stop.
    const shouldEmitTrue =
      !typingActiveRef.current || now - lastTypingTrueAtRef.current > 1500;
    if (shouldEmitTrue) {
      typingActiveRef.current = true;
      lastTypingTrueAtRef.current = now;
      emitTypingRef.current?.(conversationId, true);
    }
    if (typingIdleRef.current) clearTimeout(typingIdleRef.current);
    typingIdleRef.current = setTimeout(() => {
      typingActiveRef.current = false;
      lastTypingTrueAtRef.current = 0;
      emitTypingRef.current?.(conversationId, false);
      typingIdleRef.current = null;
    }, 2500);
  };

  const stopTyping = () => {
    if (typingIdleRef.current) {
      clearTimeout(typingIdleRef.current);
      typingIdleRef.current = null;
    }
    if (typingActiveRef.current) {
      typingActiveRef.current = false;
      lastTypingTrueAtRef.current = 0;
      emitTypingRef.current?.(conversationId, false);
    }
  };

  // Stop typing on unmount
  useEffect(() => {
    return () => {
      if (typingIdleRef.current) clearTimeout(typingIdleRef.current);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        emitTypingRef.current?.(conversationId, false);
      }
    };
  }, [conversationId]);

  const scrollToBottom = (smooth = false) => {
    const el = listRef.current;
    if (!el) return;
    navigatingToMessageRef.current = false;
    stickToBottomRef.current = true;
    setStickToBottom(true);
    setShowJump(false);
    if (smooth) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } else {
      pinChatScrollToBottom(el);
    }
  };

  const scrollToMessage = async (messageId) => {
    const container = listRef.current;
    if (!container || messageId == null) return;

    const id = Number(messageId);

    // Hold stick-to-bottom OFF for the whole jump. Otherwise early onScroll
    // events (still "near bottom") flip it back on and the messages effect
    // yanks scrollHeight — colliding with the jump-up / Jump-to-latest UI.
    navigatingToMessageRef.current = true;
    stickToBottomRef.current = false;
    setStickToBottom(false);
    setShowJump(true);

    const findTarget = () =>
      container.querySelector(`[data-dm-message-id="${id}"]`);

    // The thread only holds a recent window; page older history in until the
    // replied-to message is rendered (or we run out of history).
    let target = findTarget();
    let guard = 0;
    while (!target && guard < 25) {
      guard += 1;
      // eslint-disable-next-line no-await-in-loop
      const res = await loadOlder();
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => requestAnimationFrame(resolve));
      target = findTarget();
      if (!res?.hasMore) break;
    }
    if (!target) {
      navigatingToMessageRef.current = false;
      return;
    }

    // Instant scroll on the list container (avoids smooth-scroll races with onScroll)
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const delta = targetRect.top - containerRect.top;
    const nextTop =
      container.scrollTop +
      delta -
      container.clientHeight / 2 +
      targetRect.height / 2;

    container.scrollTop = Math.max(0, nextTop);

    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
    }
    setHighlightedMessageId(id);
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedMessageId(null);
      highlightTimerRef.current = null;
    }, 1800);

    // Release the lock after layout settles so manual scroll works again
    requestAnimationFrame(() => {
      navigatingToMessageRef.current = false;
      const el = listRef.current;
      if (!el) return;
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
      stickToBottomRef.current = nearBottom;
      setStickToBottom(nearBottom);
      setShowJump(!nearBottom);
    });
  };

  useEffect(() => {
    if (!focusMessageId || loading) return;
    scrollToMessage(focusMessageId);
    // Only jump when the deep-link target changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMessageId, conversationId, loading]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    // Don't fight an in-progress jump-to-message
    if (navigatingToMessageRef.current) return;
    const scrollTop = el.scrollTop;
    const prevTop = lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;
    // Mobile: dismiss keyboard when user scrolls up toward older messages
    if (
      useMobileComposer &&
      prevTop - scrollTop > 12 &&
      document.activeElement?.closest?.("[contenteditable]")
    ) {
      composerRef.current?.blur?.();
    }
    const nearBottom =
      el.scrollHeight - scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    stickToBottomRef.current = nearBottom;
    setStickToBottom(nearBottom);
    // Slack-style: show jump control whenever the user has scrolled away from latest
    setShowJump(!nearBottom);
  };

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const lastId =
      messages.length > 0
        ? messages[messages.length - 1]?.message_id ?? null
        : null;
    const grew =
      messages.length > prevLenRef.current ||
      (lastId != null &&
        prevLastMessageIdRef.current != null &&
        String(lastId) !== String(prevLastMessageIdRef.current));
    prevLenRef.current = messages.length;
    prevLastMessageIdRef.current = lastId;
    if (navigatingToMessageRef.current) return;
    if (stickToBottomRef.current) {
      pinChatScrollToBottom(el);
      setShowJump(false);
    } else if (grew) {
      setShowJump(true);
    }
  }, [messages, conversationId, stickToBottom, loading]);

  // Re-pin when message content grows after open (images, markdown, embeds).
  // Embed-only webhook rows often gain height after first paint; if the user
  // is not stuck to bottom, surface Jump so the new card is reachable.
  useEffect(() => {
    const el = listRef.current;
    const content = listContentRef.current;
    if (!el || !content || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (navigatingToMessageRef.current) return;
      if (stickToBottomRef.current) {
        el.scrollTop = el.scrollHeight;
        return;
      }
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
      if (!nearBottom) setShowJump(true);
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [conversationId]);

  const peerName = isSelf
    ? "You"
    : isGroup || isChannel
      ? groupTitle
      : getFullName(peer) || "Conversation";
  const peerTitle = !isSelf && !isGroup && !isChannel ? peer?.title : null;
  const peerPresence =
    !isSelf && !isGroup && !isChannel
      ? resolvePresence(peer, presenceByUserId)
      : null;

  const handleJoinChannel = async () => {
    if (!accessToken || !orgId || !conversationId || joining) return;
    setJoining(true);
    try {
      const joined = await joinChannel(accessToken, orgId, conversationId);
      onConversationChange?.(joined);
      await refresh();
    } catch (err) {
      toastError(err, "Could not join channel");
    } finally {
      setJoining(false);
    }
  };

  const typingLabel = useMemo(() => {
    const ids = [...typingUsers.keys()].filter((id) => id !== currentUserId);
    if (ids.length === 0) return null;
    const names = ids.map((id) => {
      const m = members.find((x) => x.user_id === id);
      if (m) return getFullName(m) || "Someone";
      if (peer?.user_id === id) return getFullName(peer) || "Someone";
      return "Someone";
    });
    if (names.length === 1) return `${names[0]} is typing…`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
    return `${names[0]} and ${names.length - 1} others are typing…`;
  }, [typingUsers, currentUserId, members, peer]);

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft("");
  };

  const saveEdit = async () => {
    const content = normalizeEditorMarkdown(editDraft);
    if (!editingId || !content || savingEdit) return;
    setSavingEdit(true);
    try {
      await editMessage(
        editingId,
        content,
        isSelf ? undefined : collectReferencesFromMarkdown(content, orgId)
      );
      setEditingId(null);
      setEditDraft("");
    } catch (err) {
      console.error("Failed to edit DM:", err);
      toastError(err, "Could not edit message");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (editingId || sending) return;
    let content = normalizeEditorMarkdown(
      composerRef.current?.getMarkdown?.() ?? draft
    );
    let attachment = pendingAttachment || undefined;
    if (!attachment && pendingFileCount === 0) {
      const extracted = extractPostPermalinkFromText(content, orgId);
      if (extracted) {
        attachment = extracted.attachment;
        content = stripLonePostPermalink(content, extracted.matchedUrl).trim();
      }
    }
    if (!content && !attachment && pendingFileCount === 0) return;

    // Clear immediately so Enter-to-send cannot double-fire while the request
    // is in flight (channel/mention validation can add a bit of latency).
    const outboundContent = content;
    const outboundAttachment = attachment;
    const outboundLocalFiles = pendingLocalFiles;
    const outboundTeamFiles = pendingTeamFiles;
    const outboundReply = replyTo;
    setDraft("");
    clearDmDraft(orgId, conversationId);
    composerRef.current?.clear();
    setPendingAttachment(null);
    setPendingLocalFiles([]);
    setPendingTeamFiles([]);
    setReplyTo(null);

    try {
      stopTyping();
      if (outboundLocalFiles.length || outboundTeamFiles.length) {
        await sendMedia({
          files: outboundLocalFiles,
          teamFiles: outboundTeamFiles,
          content: outboundContent,
        });
      } else {
        await send({
          content: outboundContent,
          attachment: outboundAttachment,
          reply_to_message_id: outboundReply?.message_id,
          references: isSelf
            ? undefined
            : collectReferencesFromMarkdown(outboundContent, orgId),
        });
      }
      setStickToBottom(true);
      stickToBottomRef.current = true;
      requestAnimationFrame(() => {
        composerRef.current?.focus("end");
        scrollToBottom();
      });
    } catch (err) {
      console.error("Failed to send DM:", err);
      // Restore draft on failure
      setDraft(outboundContent);
      if (orgId && conversationId && outboundContent) {
        saveDmDraft(orgId, conversationId, outboundContent);
      }
      if (outboundAttachment) setPendingAttachment(outboundAttachment);
      if (outboundLocalFiles.length) setPendingLocalFiles(outboundLocalFiles);
      if (outboundTeamFiles.length) setPendingTeamFiles(outboundTeamFiles);
      if (outboundReply) setReplyTo(outboundReply);
      composerRef.current?.setMarkdown?.(outboundContent);
      toastError(err, "Could not send message");
      requestAnimationFrame(() => composerRef.current?.focus("end"));
    }
  };

  const onComposerPaste = (e) => {
    const native = e.nativeEvent || e;
    const items = native?.clipboardData?.items;
    if (!items?.length) return;
    const files = [];
    for (const item of items) {
      if (item.kind !== "file") continue;
      const f = item.getAsFile();
      if (f) files.push(f);
    }
    if (!files.length) return;
    e.preventDefault?.();
    addLocalFiles(files);
  };

  const canDropFiles =
    !isPreview &&
    !isLocked &&
    conversation?.can_send !== false &&
    !editingId;

  const onDropZoneDragEnter = (e) => {
    if (!canDropFiles || !hasOsFiles(e.dataTransfer)) return;
    e.preventDefault();
    dropDepthRef.current += 1;
    setDropActive(true);
  };

  const onDropZoneDragLeave = (e) => {
    if (!hasOsFiles(e.dataTransfer)) return;
    e.preventDefault();
    dropDepthRef.current = Math.max(0, dropDepthRef.current - 1);
    if (dropDepthRef.current === 0) setDropActive(false);
  };

  const onDropZoneDragOver = (e) => {
    if (!canDropFiles || !hasOsFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const onDropZoneDrop = (e) => {
    e.preventDefault();
    dropDepthRef.current = 0;
    setDropActive(false);
    if (!canDropFiles) return;
    const files = e.dataTransfer?.files;
    if (files?.length) addLocalFiles(files);
  };

  const startEdit = (msg) => {
    if (!msg?.message_id) return;
    setEditingId(msg.message_id);
    setEditDraft(msg.content || "");
    requestAnimationFrame(() => {
      editEditorRef.current?.focus("end");
    });
  };

  const handleDelete = async (messageId) => {
    try {
      await removeMessage(messageId);
    } catch (err) {
      console.error("Failed to delete DM:", err);
      toastError(err, "Could not delete message");
    }
  };

  const closeMessageMenu = useCallback(() => setMessageMenu(null), []);

  const copyMessageText = useCallback(async (msg) => {
    const text = markdownToPlain(msg?.content || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toastSuccess("Copied");
    } catch (err) {
      console.error("Failed to copy message:", err);
      toastError(err, "Could not copy");
    }
  }, []);

  const startTitleEdit = () => {
    if (!canEditTitle) return;
    setTitleDraft(groupTitle);
    setEditingTitle(true);
    requestAnimationFrame(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    });
  };

  const cancelTitleEdit = () => {
    setEditingTitle(false);
    setTitleDraft("");
  };

  const saveTitleEdit = async () => {
    const title = titleDraft.trim();
    if (!title || !accessToken || !orgId || !conversationId) {
      cancelTitleEdit();
      return;
    }
    if (title === groupTitle) {
      cancelTitleEdit();
      return;
    }
    setSavingTitle(true);
    try {
      const next = isChannel
        ? await updateChannel(accessToken, orgId, conversationId, { title })
        : await updateDmGroup(accessToken, orgId, conversationId, { title });
      onConversationChange?.(
        isChannel
          ? {
              ...conversation,
              ...next,
              title: next.title,
              can_manage: next.can_manage ?? conversation?.can_manage,
            }
          : next
      );
      setEditingTitle(false);
      setTitleDraft("");
    } catch (err) {
      toastError(err, isChannel ? "Could not rename channel" : "Could not rename group");
    } finally {
      setSavingTitle(false);
    }
  };

  return (
    <div
      className={cn(
        "relative flex flex-col h-full min-h-0 min-w-0 overflow-x-hidden bg-background",
        className
      )}
      onDragEnter={canDropFiles ? onDropZoneDragEnter : undefined}
      onDragLeave={canDropFiles ? onDropZoneDragLeave : undefined}
      onDragOver={canDropFiles ? onDropZoneDragOver : undefined}
      onDrop={canDropFiles ? onDropZoneDrop : undefined}
    >
      {dropActive && canDropFiles ? (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-md border-2 border-dashed border-primary bg-primary/10">
          <p className="text-sm font-medium text-primary">Drop files to attach</p>
        </div>
      ) : null}
      {showPeerHeader && (
        <div
          className={cn(
            "flex items-center gap-3 border-b border-border px-3 shrink-0 min-w-0",
            isMobile ? "py-2" : "py-2.5"
          )}
        >
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="md:hidden shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              aria-label="Back to conversations"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          ) : null}
          <div className="relative shrink-0">
            {isSelf || isGroup || isChannel ? (
              <Avatar className={isMobile ? "h-8 w-8" : "h-10 w-10"}>
                <AvatarFallback className="text-sm">
                  {isSelf ? (
                    "Y"
                  ) : isGroup ? (
                    <Users className="h-4 w-4" />
                  ) : (
                    <Hash className="h-4 w-4" />
                  )}
                </AvatarFallback>
              </Avatar>
            ) : (
              <OrgMemberAvatar
                user={peer}
                className={isMobile ? "h-8 w-8" : "h-10 w-10"}
                fallbackClassName="text-sm font-semibold"
              />
            )}
            {peerPresence && (
              <span
                className={cn(
                  "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background",
                  peerPresence.dot
                )}
                title={peerPresence.label}
              />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              {canEditTitle && editingTitle ? (
                <Input
                  ref={titleInputRef}
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={() => {
                    if (!savingTitle) saveTitleEdit();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      saveTitleEdit();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      cancelTitleEdit();
                    }
                  }}
                  disabled={savingTitle}
                  maxLength={80}
                  className="h-8 max-w-sm font-medium text-sm"
                  aria-label={isChannel ? "Channel title" : "Group title"}
                />
              ) : canEditTitle ? (
                <button
                  type="button"
                  onClick={startTitleEdit}
                  className="font-semibold text-base truncate text-left hover:underline underline-offset-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  title="Click to rename"
                >
                  {peerName}
                </button>
              ) : (
                <p className="font-semibold text-base truncate">{peerName}</p>
              )}
              {!isMobile && isChannel && channelPublicOnPrivateTeam && (
                <span className="text-[10px] font-medium rounded-full border border-amber-500/50 text-amber-700 dark:text-amber-400 px-2 py-0.5 shrink-0">
                  Organization-wide
                </span>
              )}
              {!isMobile && isChannel && !channelPublic && (
                <span className="text-[10px] font-medium rounded-full border border-border text-muted-foreground px-2 py-0.5 shrink-0">
                  Restricted
                </span>
              )}
              {!isMobile && isPreview && (
                <span className="text-[10px] font-medium rounded-full bg-muted px-2 py-0.5 shrink-0">
                  Preview
                </span>
              )}
              {!isMobile && peerPresence && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/70 px-2 py-0.5 text-[11px] font-medium text-foreground shrink-0"
                  title={peerPresence.label}
                >
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full shrink-0",
                      peerPresence.dot
                    )}
                  />
                  {peerPresence.label}
                </span>
              )}
            </div>
            {!isMobile ? (
              isSelf ? (
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  Personal notes in this workplace
                </p>
              ) : isChannel ? (
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {conversation?.team_name
                    ? `${conversation.team_name} channel`
                    : "Team channel"}
                  {channelPublicOnPrivateTeam
                    ? " · Visible to everyone in the org"
                    : ""}
                  {conversation?.member_count != null
                    ? ` · ${conversation.member_count} members`
                    : ""}
                </p>
              ) : isGroup ? (
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {conversation?.member_count || members.length || 0} members
                </p>
              ) : (
                (() => {
                  const subtitle = [peerTitle, peer?.org_email]
                    .filter(Boolean)
                    .join(" · ");
                  return subtitle ? (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {subtitle}
                    </p>
                  ) : null;
                })()
              )
            ) : null}
          </div>
          {isMobile &&
          (isChannel ||
            isGroup ||
            peerTitle ||
            peer?.org_email ||
            peerPresence ||
            canManageChannel) ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Conversation actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-w-[16rem]">
                {isChannel ? (
                  <DropdownMenuItem
                    disabled
                    className="text-xs text-muted-foreground focus:bg-transparent"
                  >
                    {conversation?.team_name
                      ? `${conversation.team_name} channel`
                      : "Team channel"}
                    {conversation?.member_count != null
                      ? ` · ${conversation.member_count} members`
                      : ""}
                  </DropdownMenuItem>
                ) : isGroup ? (
                  <DropdownMenuItem
                    disabled
                    className="text-xs text-muted-foreground focus:bg-transparent"
                  >
                    {conversation?.member_count || members.length || 0} members
                  </DropdownMenuItem>
                ) : peerTitle || peer?.org_email || peerPresence ? (
                  <DropdownMenuItem
                    disabled
                    className="text-xs text-muted-foreground focus:bg-transparent"
                  >
                    {[peerTitle, peer?.org_email, peerPresence?.label]
                      .filter(Boolean)
                      .join(" · ")}
                  </DropdownMenuItem>
                ) : null}
                {(isGroup || (isChannel && !isPreview && !isLocked)) && (
                  <DropdownMenuItem onClick={() => setMembersOpen(true)}>
                    <Users className="h-4 w-4 mr-2" />
                    Members
                  </DropdownMenuItem>
                )}
                {canManageChannel && !isLocked ? (
                  <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : !isMobile ? (
            <>
              {(isGroup || (isChannel && !isPreview && !isLocked)) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={() => setMembersOpen(true)}
                >
                  <Users className="h-4 w-4 mr-1" />
                  Members
                </Button>
              )}
              {canManageChannel && !isLocked && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  title="Channel settings"
                  onClick={() => setSettingsOpen(true)}
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Settings
                </Button>
              )}
            </>
          ) : null}
          {headerExtra}
        </div>
      )}
      {!showPeerHeader && (isGroup || (isChannel && !isPreview && !isLocked) || canManageChannel) ? (
        <div className="flex items-center justify-end gap-2 px-3 py-1 border-b border-border shrink-0">
          {(isGroup || (isChannel && !isPreview && !isLocked)) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 text-xs"
              onClick={() => setMembersOpen(true)}
            >
              <Users className="h-3.5 w-3.5 mr-1" />
              Members
            </Button>
          )}
          {canManageChannel && !isLocked && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 text-xs"
              title="Channel settings"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="h-3.5 w-3.5 mr-1" />
              Settings
            </Button>
          )}
        </div>
      ) : null}

      <div className="relative flex-1 min-h-0 min-w-0 overflow-x-hidden">
        <div
          ref={listRef}
          onScroll={onScroll}
          className={cn(
            "h-full min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-hide px-3 py-2",
            compact ? "text-sm" : ""
          )}
        >
          <div
            ref={listContentRef}
            className={cn(
              "min-w-0 w-full",
              isMobile ? "space-y-1" : "space-y-4"
            )}
          >
          {loading && messages.length === 0 ? (
            <p className="text-sm text-muted-foreground">Loading messages…</p>
          ) : isLocked ? (
            <div className="flex flex-col items-center justify-center min-h-[12rem] py-12 px-6 text-center">
              <Lock className="h-8 w-8 text-muted-foreground mb-3" />
              <p className="font-medium">You don&apos;t have access to this channel</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                This restricted channel is invite-only. Ask a member to invite
                you if you need access.
              </p>
            </div>
          ) : messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No messages yet. Say hello.
            </p>
          ) : (
            messages.map((msg, msgIndex) => {
              const cluster = getMessageClusterMeta(messages, msgIndex, {
                systemSender: SYSTEM_SENDER,
              });
              if (msg.sender_id === SYSTEM_SENDER) {
                return (
                  <div
                    key={msg.message_id}
                    className="flex justify-center px-2"
                  >
                    <div className="max-w-[85%] rounded-md px-2.5 py-2 bg-muted/50 text-muted-foreground text-xs leading-snug">
                      {msg.content ? (
                        <MarkdownBody
                          content={msg.content}
                          compact
                          className="text-xs text-muted-foreground text-left [&_strong]:font-semibold [&_p]:text-left [&_ul]:text-left [&_ol]:text-left"
                          membersById={membersById}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              }

              if (msg.deleted_at) {
                const deletedMine = msg.sender_id === currentUserId;
                return (
                  <div
                    key={msg.message_id}
                    id={`dm-msg-${msg.message_id}`}
                    data-dm-message-id={msg.message_id}
                  className={cn(
                    "flex w-full min-w-0 items-start scroll-mt-4 rounded-md transition-colors duration-500",
                    isMobile ? "gap-2" : "gap-3",
                    deletedMine ? "justify-end" : "justify-start",
                    isMobile && cluster.startOfRun && "mt-2",
                    highlightedMessageId === msg.message_id &&
                      "bg-primary/15 ring-1 ring-primary/40"
                  )}
                  >
                    {!deletedMine &&
                    (!isMobile || isGroup || isChannel) ? (
                      isMobile && !cluster.startOfRun ? (
                        <div className="w-7 shrink-0" aria-hidden />
                      ) : (
                        <MemberProfileHover
                          user={msg.sender}
                          presenceByUserId={presenceByUserId}
                          side="right"
                        >
                          <OrgMemberAvatar
                            user={msg.sender}
                            className={cn(
                              "shrink-0 mt-0.5",
                              isMobile ? "h-7 w-7" : "h-9 w-9"
                            )}
                            fallbackClassName="text-xs font-semibold"
                          />
                        </MemberProfileHover>
                      )
                    ) : null}
                    <div
                      className={cn(
                        "max-w-[85%] bg-muted/50 text-muted-foreground italic text-xs leading-snug",
                        isMobile
                          ? "rounded-2xl px-3 py-1"
                          : "rounded-md px-2 py-0.5"
                      )}
                    >
                      This message was deleted
                    </div>
                  </div>
                );
              }

              const mine = msg.sender_id === currentUserId;
              const isWebhookBot =
                msg.sender_id === "webhook" || !!msg.sender?.is_bot;
              const canDeleteMsg =
                mine || (isWebhookBot && canManageIntegrations);
              const isEditing = editingId === msg.message_id;
              const showIncomingAvatar =
                !mine &&
                (!isMobile || isGroup || isChannel) &&
                (!isMobile || cluster.startOfRun);
              const reserveAvatarSlot =
                !mine && isMobile && (isGroup || isChannel) && !cluster.startOfRun;
              const showSenderName =
                !mine &&
                !isEditing &&
                (!isMobile || cluster.startOfRun) &&
                (!isMobile || isGroup || isChannel);
              const showTimestamp =
                !isEditing && (!isMobile || cluster.showTimestamp);
              const hasTextBody = !!(
                msg.content && String(msg.content).trim()
              );
              const showMessageBubble =
                isEditing ||
                !!msg.reply_to ||
                hasTextBody ||
                !!msg.attachment;

              return (
                <div
                  key={msg.message_id}
                  id={`dm-msg-${msg.message_id}`}
                  data-dm-message-id={msg.message_id}
                  className={cn(
                    "flex w-full min-w-0 items-start scroll-mt-4 rounded-md transition-colors duration-500",
                    isMobile ? "gap-2" : "gap-3",
                    mine ? "justify-end" : "justify-start",
                    isMobile && cluster.startOfRun && "mt-2",
                    highlightedMessageId === msg.message_id &&
                      "bg-primary/15 ring-1 ring-primary/40"
                  )}
                >
                {showIncomingAvatar ? (
                  <MemberProfileHover
                    user={msg.sender}
                    presenceByUserId={presenceByUserId}
                    side="right"
                  >
                    <OrgMemberAvatar
                      user={msg.sender}
                      className={cn(
                        "shrink-0 mt-0.5",
                        isMobile ? "h-7 w-7" : "h-9 w-9"
                      )}
                      fallbackClassName="text-xs font-semibold"
                    />
                  </MemberProfileHover>
                ) : reserveAvatarSlot ? (
                  <div className="w-7 shrink-0" aria-hidden />
                ) : null}
                <DmMessageContextTarget
                  enabled={!isEditing && !isPreview && !isLocked}
                  className={cn(
                    "max-w-[min(85%,100%)] min-w-0 flex flex-col relative group overflow-visible md:pt-8",
                    mine ? "items-end" : "items-start"
                  )}
                  onOpen={({ x, y }) =>
                    setMessageMenu({
                      msg,
                      x,
                      y,
                      mine,
                      canDelete: canDeleteMsg,
                    })
                  }
                >
                {!isEditing ? (
                  <div
                    className={cn(
                      "absolute z-10 top-0 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center gap-0.5",
                      mine ? "left-0" : "right-0"
                    )}
                  >
                    {!(isChannel && conversation?.can_send === false) &&
                    !isPreview &&
                    !isLocked ? (
                      <button
                        type="button"
                        title="Reply"
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-muted"
                        onClick={() => setReplyTo(msg)}
                      >
                        <Reply className="h-3.5 w-3.5 pointer-events-none" />
                      </button>
                    ) : null}
                    {!isPreview && !isLocked ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          title="React"
                          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-muted"
                        >
                          <SmilePlus className="h-3.5 w-3.5 pointer-events-none" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="flex w-auto min-w-0 flex-row gap-0.5 p-1.5"
                      >
                        {DM_QUICK_REACT_EMOJIS.map((emoji) => (
                          <DropdownMenuItem
                            key={emoji}
                            className="h-8 w-8 cursor-pointer justify-center p-0 text-base"
                            onSelect={() => {
                              void reactToMessage(msg.message_id, emoji);
                            }}
                          >
                            {emoji}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    ) : null}
                    {canDeleteMsg && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label="Message actions"
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-muted"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5 pointer-events-none" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {mine ? (
                            <DropdownMenuItem onClick={() => startEdit(msg)}>
                              <Pencil className="h-3.5 w-3.5 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => handleDelete(msg.message_id)}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ) : null}
                {(showSenderName || showTimestamp) && !isEditing ? (
                  <div
                    className={cn(
                      "flex items-baseline gap-2 mb-0.5 px-0.5 max-w-full min-w-0",
                      mine ? "flex-row-reverse" : ""
                    )}
                  >
                    {showSenderName ? (
                      <span
                        className={cn(
                          "inline-flex items-baseline gap-1.5 min-w-0 max-w-full",
                          mine ? "flex-row-reverse" : ""
                        )}
                      >
                        <MemberProfileHover
                          user={msg.sender}
                          presenceByUserId={presenceByUserId}
                          side="bottom"
                          className="min-w-0 max-w-full"
                        >
                          <button
                            type="button"
                            className="text-sm font-semibold leading-tight text-foreground text-left hover:underline underline-offset-2 truncate max-w-full"
                          >
                            {getFullName(msg.sender) ||
                              msg.sender?.display_name ||
                              "Bot"}
                          </button>
                        </MemberProfileHover>
                        {msg.sender?.is_bot ||
                        msg.sender_id === "webhook" ? (
                          <span className="text-[10px] font-medium uppercase tracking-wide text-sky-600 dark:text-sky-400 shrink-0">
                            Bot
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    {showTimestamp ? (
                      <p className="text-[10px] text-muted-foreground leading-none shrink-0 whitespace-nowrap">
                        {msg.edited_at ? "Edited · " : ""}
                        {formatDmTimestamp(msg.created_at, {
                          compact: isMobile,
                        })}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {showMessageBubble ? (
                <div
                  className={cn(
                    "relative leading-snug w-fit max-w-full min-w-0 break-words overflow-x-hidden",
                    isMobile
                      ? cn(
                          "rounded-2xl px-3 py-2",
                          mine
                            ? "rounded-br-md bg-primary/20 text-foreground"
                            : "rounded-bl-md bg-muted text-foreground"
                        )
                      : cn(
                          "rounded-md px-2.5 py-1.5",
                          mine
                            ? "bg-primary/20 text-foreground border border-primary/35"
                            : "bg-muted text-foreground"
                        )
                  )}
                >
                    {msg.reply_to && !isEditing && (
                      <button
                        type="button"
                        disabled={!!msg.reply_to.deleted || !msg.reply_to.message_id}
                        onClick={() => scrollToMessage(msg.reply_to.message_id)}
                        className={cn(
                          "mb-0.5 w-full rounded border-l-2 px-1.5 py-0.5 text-left text-[11px] leading-snug opacity-90 transition-colors",
                          mine
                            ? "border-primary/50 bg-primary/10 text-foreground"
                            : "border-foreground/30 bg-background/50",
                          !msg.reply_to.deleted &&
                            msg.reply_to.message_id &&
                            "cursor-pointer hover:opacity-100 hover:bg-primary/20",
                          (msg.reply_to.deleted || !msg.reply_to.message_id) &&
                            "cursor-default"
                        )}
                        title={
                          msg.reply_to.deleted
                            ? "Original message deleted"
                            : "Jump to original message"
                        }
                        aria-label={
                          msg.reply_to.deleted
                            ? "Original message deleted"
                            : "Jump to original message"
                        }
                      >
                        {msg.reply_to.deleted
                          ? "Original message deleted"
                          : plainExcerpt(
                              msg.reply_to.content_preview,
                              120
                            ) || "Attachment"}
                      </button>
                    )}
                    {isEditing ? (
                      <div className="space-y-2 min-w-[min(100%,280px)] w-full max-w-md">
                        <div className="flex items-end gap-1">
                          <EmojiInsertButton
                            disabled={savingEdit}
                            onInsert={(native) => {
                              editEditorRef.current?.insertText(native);
                            }}
                          />
                          <RichMessageEditor
                            key={`edit-${msg.message_id}`}
                            ref={editEditorRef}
                            value={editDraft}
                            onChange={setEditDraft}
                            onSubmit={saveEdit}
                            onEscape={cancelEdit}
                            disabled={savingEdit}
                            placeholder="Edit message…"
                            minRows={2}
                            autoFocus
                            className="flex-1"
                            referenceContext={referenceContext}
                          />
                        </div>
                        <div className="flex gap-1 justify-end">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={cancelEdit}
                            disabled={savingEdit}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={saveEdit}
                            disabled={
                              savingEdit || isMarkdownEmpty(editDraft)
                            }
                            title="Save (Enter)"
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {hasTextBody ? (
                          <div className="max-w-none break-words">
                            <MarkdownBody
                              content={msg.content}
                              compact
                              inverse={false}
                              membersById={membersById}
                            />
                          </div>
                        ) : null}
                        <DmGifAttachment
                          attachment={msg.attachment}
                          className={isMobile ? "max-h-48 rounded-xl" : undefined}
                        />
                        <DmFileAttachments
                          attachment={msg.attachment}
                          orgId={orgId}
                        />
                        <DmPostAttachmentCard
                          attachment={msg.attachment}
                          orgId={orgId}
                          inverse={false}
                          compact={compact}
                        />
                      </>
                    )}
                </div>
                ) : null}
                {!isEditing && msg.embed ? (
                  <CircatronEmbedCard
                    embed={msg.embed}
                    membersById={membersById}
                    compact={compact || isMobile}
                    className={showMessageBubble ? "mt-1" : undefined}
                  />
                ) : null}
                {!isEditing &&
                Array.isArray(msg.circatron_actions) &&
                msg.circatron_actions.length > 0 ? (
                  <div
                    className={cn(
                      "flex flex-wrap gap-1.5",
                      showMessageBubble || msg.embed ? "mt-1.5" : "mt-1"
                    )}
                  >
                    {msg.circatron_actions.map((action) => {
                      const consumed = !!msg.circatron_interaction?.consumed_at;
                      const expired =
                        msg.circatron_interaction?.expires_at &&
                        Date.parse(msg.circatron_interaction.expires_at) <
                          Date.now();
                      const disabled = consumed || expired;
                      return (
                        <Button
                          key={`${msg.message_id}-${action.action_id}`}
                          type="button"
                          size="sm"
                          variant={
                            action.style === "primary" ? "default" : "outline"
                          }
                          className="h-7 text-xs"
                          disabled={disabled}
                          onClick={async () => {
                            try {
                              await postCircatronInteractionCallback(
                                accessToken,
                                orgId,
                                {
                                  message_id: msg.message_id,
                                  action_id: action.action_id,
                                  conversation_id: conversationId,
                                }
                              );
                              await refresh?.();
                            } catch (err) {
                              toastError(err, "Could not send action");
                            }
                          }}
                        >
                          {action.label}
                        </Button>
                      );
                    })}
                  </div>
                ) : null}
                {!isEditing ? (
                  <DmMessageReactions
                    reactions={msg.reactions}
                    inverse={false}
                    compact={compact}
                    align={mine ? "end" : "start"}
                    onReact={(emoji) =>
                      reactToMessage(msg.message_id, emoji)
                    }
                  />
                ) : null}
                {mine &&
                !isEditing &&
                seenReceiptMessageId === msg.message_id ? (
                  <p className="text-[10px] text-muted-foreground mt-0.5 px-0.5">
                    Seen
                  </p>
                ) : null}
                </DmMessageContextTarget>
                </div>
              );
            })
          )}
          </div>
        </div>
        {showJump && (
          <button
            type="button"
            onClick={() => scrollToBottom(true)}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 rounded-full border border-border bg-background/95 backdrop-blur-sm px-3.5 py-2 text-xs font-medium shadow-lg hover:bg-muted transition-colors"
            aria-label="Jump to latest messages"
          >
            <ArrowDown className="h-3.5 w-3.5" />
            Jump to latest
          </button>
        )}
      </div>

      {typingLabel && !isPreview ? (
        <p className="text-xs text-muted-foreground px-3 pt-2 pb-0 shrink-0 truncate">
          {typingLabel}
        </p>
      ) : null}

      {isLocked ? (
        <div className="border-t border-border p-3 shrink-0 bg-muted/30 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] md:pb-3">
          <p className="text-sm text-muted-foreground text-center">
            You can&apos;t send messages in this channel without an invitation.
          </p>
        </div>
      ) : isPreview ? (
        <div className="border-t border-border p-3 shrink-0 flex items-center justify-between gap-3 bg-muted/30 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] md:pb-3">
          <p className="text-sm text-muted-foreground">
            {conversation?.posting_policy === "team_only" ||
            conversation?.posting_policy === "custom"
              ? "You're previewing this organization-wide channel. Join to follow and react."
              : "You're previewing this organization-wide channel. Join to send messages."}
          </p>
          <Button
            type="button"
            size="sm"
            onClick={handleJoinChannel}
            disabled={joining}
          >
            {joining ? "Joining…" : "Join channel"}
          </Button>
        </div>
      ) : isChannel && conversation?.can_send === false ? (
        <div className="border-t border-border p-3 shrink-0 bg-muted/30 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] md:pb-3">
          <p className="text-sm text-muted-foreground text-center">
            {conversation?.posting_policy === "custom"
              ? "Only selected people can post. You can still react."
              : "Only team members can post. You can still react."}
          </p>
        </div>
      ) : (
      <form
        onSubmit={handleSend}
        className="border-t border-border px-2 pt-2 shrink-0 space-y-2 min-w-0 overflow-x-hidden pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] md:p-2"
      >
        {replyTo && (
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs">
            <Reply className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate flex-1">
              Replying to:{" "}
              {plainExcerpt(replyTo.content, 80) || "Attachment"}
            </span>
            <button
              type="button"
              className="p-0.5 hover:opacity-70"
              onClick={() => setReplyTo(null)}
              aria-label="Cancel reply"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {pendingAttachment?.type === "gif" && (
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs">
            <img
              src={
                pendingAttachment.preview_url || pendingAttachment.url
              }
              alt=""
              className="h-24 w-24 rounded object-cover shrink-0"
            />
            <span className="truncate flex-1">
              {pendingAttachment.title || "GIF"}
            </span>
            <button
              type="button"
              className="p-0.5 hover:opacity-70"
              onClick={() => setPendingAttachment(null)}
              aria-label="Remove attachment"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {pendingFileCount > 0 && (
          <div className="space-y-1.5">
            <div className="flex flex-wrap gap-1.5">
              {pendingLocalFiles.map((file, idx) => (
                <PendingLocalChip
                  key={`local-${file.name}-${file.size}-${idx}`}
                  file={file}
                  onRemove={() =>
                    setPendingLocalFiles((prev) =>
                      prev.filter((_, i) => i !== idx)
                    )
                  }
                />
              ))}
              {pendingTeamFiles.map((ref, idx) => (
                <div
                  key={`tf-${ref.team_id}-${ref.node_id}`}
                  className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs max-w-full"
                >
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate flex-1">
                    {ref.name || "Team file"}
                  </span>
                  <button
                    type="button"
                    className="p-0.5 hover:opacity-70"
                    onClick={() =>
                      setPendingTeamFiles((prev) =>
                        prev.filter((_, i) => i !== idx)
                      )
                    }
                    aria-label="Remove Team File"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            {!isChannel &&
            pendingTeamFiles.some((t) => t.visibility === "internal") ? (
              <p className="text-[11px] text-muted-foreground">
                People who are not on the team cannot open Internal files.
              </p>
            ) : null}
          </div>
        )}
        <div
          ref={composerWrapRef}
          className={cn(
            "relative gap-1",
            useMobileComposer ? "flex flex-col" : "flex items-end"
          )}
        >
          {slashMenuOpen ? (
            <SlashCommandSuggestionList
              items={filteredSlashCommands}
              selectedIndex={slashSelectedIndex}
              loading={slashLoading}
              emptyText={
                isPersonalBotDm
                  ? "No slash commands for this bot"
                  : "No slash commands in this channel"
              }
              anchorRect={
                composerWrapRef.current?.getBoundingClientRect?.() || null
              }
              onHover={setSlashSelectedIndex}
              onSelect={applySlashCommand}
            />
          ) : null}
          {useMobileComposer ? (
            <MobileMessageComposer
              composerRef={composerRef}
              editorKey={`compose-${conversationDraftKey}`}
              value={draft}
              onChange={setDraft}
              onUserInput={signalTyping}
              onSubmit={handleSend}
              onPaste={onComposerPaste}
              disabled={sending || !!editingId}
              sendDisabled={sending || !!editingId}
              placeholder="Write a message…"
              referenceContext={referenceContext}
              orgId={orgId}
              onEditorKeyDown={onSlashEditorKeyDown}
              onAttach={() => setAttachOpen(true)}
              attachDisabled={
                !!editingId ||
                Boolean(pendingAttachment) ||
                remainingAttach <= 0
              }
              onGifSelect={(gif) => {
                setPendingLocalFiles([]);
                setPendingTeamFiles([]);
                setPendingAttachment(gif);
              }}
              gifDisabled={!!editingId || sending || pendingFileCount > 0}
              onEmojiInsert={(native) => {
                composerRef.current?.insertText(native);
                signalTyping();
              }}
            />
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => setAttachOpen(true)}
                title="Attach files"
                disabled={
                  !!editingId ||
                  Boolean(pendingAttachment) ||
                  remainingAttach <= 0
                }
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <GifPickerButton
                orgId={orgId}
                disabled={!!editingId || sending || pendingFileCount > 0}
                onSelect={(gif) => {
                  setPendingLocalFiles([]);
                  setPendingTeamFiles([]);
                  setPendingAttachment(gif);
                }}
              />
              <EmojiInsertButton
                disabled={sending || !!editingId}
                onInsert={(native) => {
                  composerRef.current?.insertText(native);
                  signalTyping();
                }}
              />
              <div onPaste={onComposerPaste} className="flex-1">
                <RichMessageEditor
                  key={`compose-${conversationDraftKey}`}
                  ref={composerRef}
                  value={draft}
                  onChange={setDraft}
                  onUserInput={signalTyping}
                  onSubmit={handleSend}
                  disabled={sending || !!editingId}
                  placeholder="Write a message…"
                  minRows={3}
                  className="flex-1"
                  referenceContext={referenceContext}
                  onEditorKeyDown={onSlashEditorKeyDown}
                />
              </div>
              <Button
                type="button"
                size="icon"
                className="h-8 w-8 shrink-0"
                disabled={sending || !!editingId}
                title="Send (Enter)"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSend}
              >
                <Send className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </form>
      )}

      <DmAttachPickerDialog
        open={attachOpen}
        onOpenChange={setAttachOpen}
        accessToken={accessToken}
        orgId={orgId}
        channelTeamId={Number.isFinite(channelTeamId) ? channelTeamId : null}
        remaining={remainingAttach}
        onPickLocalFiles={addLocalFiles}
        onPickTeamFiles={addTeamFiles}
      />
      {isGroup && (
        <GroupMembersDialog
          open={membersOpen}
          onOpenChange={setMembersOpen}
          orgId={orgId}
          conversation={conversation}
          accessToken={accessToken}
          currentUserId={currentUserId}
          isOwner={isOwner}
          onUpdated={(next) => onConversationChange?.(next)}
        />
      )}
      {isChannel && !isPreview && !isLocked && (
        <ChannelMembersDialog
          open={membersOpen}
          onOpenChange={setMembersOpen}
          orgId={orgId}
          conversation={conversation}
          accessToken={accessToken}
          currentUserId={currentUserId}
          onUpdated={(next) => onConversationChange?.(next)}
        />
      )}
      {canManageChannel && (
        <ChannelSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          orgId={orgId}
          conversation={conversation}
          accessToken={accessToken}
          onUpdated={(next) => onConversationChange?.(next)}
        />
      )}

      <InviteAndMentionDialog
        open={Boolean(invitePending)}
        onOpenChange={(open) => {
          if (!open && !inviteLoading) {
            setInvitePending(null);
            setInviteError(null);
          }
        }}
        displayName={invitePending?.item?.displayName}
        mode={invitePending?.mode || "invite"}
        loading={inviteLoading}
        error={inviteError}
        onConfirm={confirmInviteAndMention}
      />

      <DmMessageContextMenu
        open={Boolean(messageMenu?.msg)}
        x={messageMenu?.x ?? 0}
        y={messageMenu?.y ?? 0}
        onClose={closeMessageMenu}
        canReply={
          Boolean(messageMenu?.msg) &&
          !(isChannel && conversation?.can_send === false) &&
          !isPreview &&
          !isLocked
        }
        canReact={Boolean(messageMenu?.msg) && !isPreview && !isLocked}
        reactEmojis={DM_QUICK_REACT_EMOJIS}
        canCopy={Boolean(
          markdownToPlain(messageMenu?.msg?.content || "").trim()
        )}
        canEdit={Boolean(messageMenu?.mine)}
        canDelete={Boolean(messageMenu?.canDelete)}
        onReply={() => setReplyTo(messageMenu.msg)}
        onReact={(emoji) =>
          reactToMessage(messageMenu.msg.message_id, emoji)
        }
        onCopy={() => copyMessageText(messageMenu.msg)}
        onEdit={() => startEdit(messageMenu.msg)}
        onDelete={() => handleDelete(messageMenu.msg.message_id)}
      />
    </div>
  );
}

function GroupMembersDialog({
  open,
  onOpenChange,
  orgId,
  conversation,
  accessToken,
  currentUserId,
  isOwner,
  onUpdated,
}) {
  const [renameDraft, setRenameDraft] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [memberQuery, setMemberQuery] = useState("");
  const [orgUsers, setOrgUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);
  const members = conversation?.members || [];
  const memberIds = new Set(members.map((m) => m.user_id));

  useEffect(() => {
    if (!open) return;
    setRenameDraft(conversation?.title || "");
    setMemberQuery("");
  }, [open, conversation?.title]);

  useEffect(() => {
    if (!open || !orgId || !accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!cancelled) {
          setOrgUsers(
            (res.data || [])
              .map((m) => {
                const u = m.user || m;
                if (!u?.user_id) return null;
                return {
                  ...u,
                  org_email: m.org_email ?? u.org_email ?? null,
                  title: m.title ?? u.title ?? null,
                };
              })
              .filter(Boolean)
          );
        }
      } catch (err) {
        console.error("Failed to load org users:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, accessToken]);

  const candidates = orgUsers.filter((u) => {
    if (memberIds.has(u.user_id)) return false;
    const q = memberQuery.trim().toLowerCase();
    if (!q) return true;
    const name = getFullName(u).toLowerCase();
    return (
      name.includes(q) ||
      (u.org_email || "").toLowerCase().includes(q) ||
      (u.title || "").toLowerCase().includes(q)
    );
  });

  const handleRename = async () => {
    const title = renameDraft.trim();
    if (!title || title === conversation?.title) return;
    setRenaming(true);
    try {
      const next = await updateDmGroup(
        accessToken,
        orgId,
        conversation.conversation_id,
        { title }
      );
      onUpdated?.(next);
    } catch (err) {
      toastError(err, "Could not rename group");
    } finally {
      setRenaming(false);
    }
  };

  const handleAdd = async (userId) => {
    setAdding(true);
    try {
      const next = await addDmGroupMembers(
        accessToken,
        orgId,
        conversation.conversation_id,
        [userId]
      );
      onUpdated?.(next);
    } catch (err) {
      toastError(err, "Could not add member");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId) => {
    setBusyUserId(userId);
    try {
      const next = await removeDmGroupMember(
        accessToken,
        orgId,
        conversation.conversation_id,
        userId
      );
      if (userId === currentUserId || next?.left) {
        onOpenChange(false);
        onUpdated?.(next?.left ? { ...conversation, left: true } : next);
        return;
      }
      onUpdated?.(next);
    } catch (err) {
      toastError(err, "Could not update members");
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Group members</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2">
          <Input
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            placeholder="Group title"
            maxLength={80}
          />
          <Button
            type="button"
            size="sm"
            disabled={
              renaming ||
              !renameDraft.trim() ||
              renameDraft.trim() === conversation?.title
            }
            onClick={handleRename}
          >
            Rename
          </Button>
        </div>
        <ul className="max-h-48 overflow-y-auto divide-y border rounded-md">
          {members.map((m) => (
            <li
              key={m.user_id}
              className="flex items-center gap-2 px-3 py-2 text-sm"
            >
              <OrgMemberAvatar
                user={m}
                className="h-7 w-7"
                fallbackClassName="text-[10px]"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {getFullName(m)}
                  {m.user_id === currentUserId ? " (you)" : ""}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {m.role === "owner" ? "Owner" : "Member"}
                  {m.title ? ` · ${m.title}` : ""}
                </p>
              </div>
              {m.user_id === currentUserId ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busyUserId === m.user_id}
                  onClick={() => handleRemove(m.user_id)}
                >
                  Leave
                </Button>
              ) : isOwner ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busyUserId === m.user_id}
                  onClick={() => handleRemove(m.user_id)}
                >
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
        <div className="space-y-2">
          <p className="text-sm font-medium">Add people</p>
          <Input
            value={memberQuery}
            onChange={(e) => setMemberQuery(e.target.value)}
            placeholder="Search members…"
          />
          <ul className="max-h-36 overflow-y-auto divide-y border rounded-md">
            {candidates.slice(0, 20).map((u) => (
              <li
                key={u.user_id}
                className="flex items-center gap-2 px-3 py-2 text-sm"
              >
                <span className="truncate flex-1">{getFullName(u)}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={adding}
                  onClick={() => handleAdd(u.user_id)}
                >
                  Add
                </Button>
              </li>
            ))}
            {candidates.length === 0 && (
              <li className="px-3 py-2 text-xs text-muted-foreground">
                No matching people to add
              </li>
            )}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChannelSettingsDialog({
  open,
  onOpenChange,
  orgId,
  conversation,
  accessToken,
  onUpdated,
}) {
  const [title, setTitle] = useState("");
  const [postingPolicy, setPostingPolicy] = useState("open");
  const [posterIds, setPosterIds] = useState([]);
  const [posterUsers, setPosterUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const isDefault = !!conversation?.is_default;
  const currentUserId = useMemo(() => {
    try {
      if (!accessToken) return null;
      return jwtDecode(accessToken)?.sub || null;
    } catch {
      return null;
    }
  }, [accessToken]);

  useEffect(() => {
    if (!open || !accessToken || !orgId || !conversation?.conversation_id) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ch = await getChannel(
          accessToken,
          orgId,
          conversation.conversation_id
        );
        if (cancelled || !ch) return;
        setTitle(ch.title || conversation?.title || "");
        setPostingPolicy(ch.posting_policy || "open");
        setPosterIds(ch.allowed_poster_ids || []);
        setPosterUsers(ch.allowed_posters || []);
      } catch {
        setTitle(conversation?.title || "");
        setPostingPolicy(conversation?.posting_policy || "open");
        setPosterIds(conversation?.allowed_poster_ids || []);
        setPosterUsers(conversation?.allowed_posters || []);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Sync settings when dialog opens or conversation id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accessToken, orgId, conversation?.conversation_id]);

  const handleSave = async () => {
    if (!accessToken || !orgId || !conversation?.conversation_id || saving) {
      return;
    }
    const titleTrimmed = title.trim();
    if (!isDefault && !titleTrimmed) return;
    if (postingPolicy === "custom" && posterIds.length === 0) {
      toastError("Select at least one person who can post");
      return;
    }

    const patch = {};
    if (!isDefault && titleTrimmed !== (conversation.title || "").trim()) {
      patch.title = titleTrimmed;
    }
    const prevPolicy = conversation.posting_policy || "open";
    const prevPosters = conversation.allowed_poster_ids || [];
    if (postingPolicy !== prevPolicy) {
      patch.posting_policy = postingPolicy;
    }
    if (postingPolicy === "custom") {
      const samePosters =
        posterIds.length === prevPosters.length &&
        posterIds.every((id) => prevPosters.includes(id));
      if (!samePosters || postingPolicy !== prevPolicy) {
        patch.posting_policy = postingPolicy;
        patch.allowed_poster_ids = posterIds;
      }
    }
    if (!Object.keys(patch).length) {
      onOpenChange(false);
      return;
    }

    setSaving(true);
    try {
      const next = await updateChannel(
        accessToken,
        orgId,
        conversation.conversation_id,
        patch
      );
      toastSuccess("Channel updated");
      onUpdated?.({
        ...conversation,
        ...next,
        title: next.title ?? patch.title ?? conversation.title,
        posting_policy:
          next.posting_policy ??
          patch.posting_policy ??
          conversation.posting_policy,
        allowed_poster_ids:
          next.allowed_poster_ids ??
          patch.allowed_poster_ids ??
          conversation.allowed_poster_ids,
        allowed_posters: next.allowed_posters ?? conversation.allowed_posters,
        can_send: next.can_send ?? conversation.can_send,
        can_manage: next.can_manage ?? conversation.can_manage,
        is_default: next.is_default ?? conversation.is_default,
      });
      onOpenChange(false);
    } catch (err) {
      toastError(err, "Could not update channel");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Channel settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {!isDefault && (
            <div className="space-y-1.5">
              <Label htmlFor="channel-settings-title">Name</Label>
              <Input
                id="channel-settings-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={80}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSave();
                  }
                }}
              />
            </div>
          )}
          <ChannelPostingPolicyFields
            accessToken={accessToken}
            orgId={orgId}
            postingPolicy={postingPolicy}
            onPostingPolicyChange={(next) => {
              setPostingPolicy(next);
              if (next === "custom" && posterIds.length === 0 && currentUserId) {
                setPosterIds([currentUserId]);
              }
            }}
            selectedIds={posterIds}
            selectedUsers={posterUsers}
            onSelectedIdsChange={(ids, users) => {
              setPosterIds(ids);
              if (users) setPosterUsers(users);
            }}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              saving ||
              (!isDefault && !title.trim()) ||
              (postingPolicy === "custom" && posterIds.length === 0)
            }
            onClick={handleSave}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelMembersDialog({
  open,
  onOpenChange,
  orgId,
  conversation,
  accessToken,
  currentUserId,
  onUpdated,
}) {
  const [memberQuery, setMemberQuery] = useState("");
  const [orgUsers, setOrgUsers] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);
  const members = conversation?.members || [];
  const memberIds = new Set(members.map((m) => m.user_id));
  const memberCount =
    conversation?.member_count != null
      ? conversation.member_count
      : members.length;

  useEffect(() => {
    if (!open) return;
    setMemberQuery("");
  }, [open]);

  useEffect(() => {
    if (!open || !orgId || !accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!cancelled) {
          setOrgUsers(
            (res.data || [])
              .map((m) => {
                const u = m.user || m;
                if (!u?.user_id) return null;
                return {
                  ...u,
                  org_email: m.org_email ?? u.org_email ?? null,
                  title: m.title ?? u.title ?? null,
                };
              })
              .filter(Boolean)
          );
        }
      } catch (err) {
        console.error("Failed to load org users:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, accessToken]);

  const candidates = orgUsers.filter((u) => {
    if (memberIds.has(u.user_id)) return false;
    const q = memberQuery.trim().toLowerCase();
    if (!q) return true;
    const name = getFullName(u).toLowerCase();
    return (
      name.includes(q) ||
      (u.org_email || "").toLowerCase().includes(q) ||
      (u.title || "").toLowerCase().includes(q)
    );
  });

  const handleInvite = async (user) => {
    if (!user?.user_id || !conversation?.conversation_id) return;
    if (conversation?.is_member === false) {
      toastError("You must be a channel member to invite others");
      return;
    }
    setInviting(true);
    try {
      await inviteToChannel(
        accessToken,
        orgId,
        conversation.conversation_id,
        user.user_id
      );
      onUpdated?.({
        ...conversation,
        members: [
          ...members,
          {
            user_id: user.user_id,
            first_name: user.first_name,
            last_name: user.last_name,
            org_email: user.org_email ?? null,
            title: user.title,
            role: "member",
          },
        ],
        member_count: memberCount + 1,
      });
    } catch (err) {
      toastError(err, "Could not invite member");
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId) => {
    setBusyUserId(userId);
    try {
      await removeChannelMember(
        accessToken,
        orgId,
        conversation.conversation_id,
        userId
      );
      if (userId === currentUserId) {
        onOpenChange(false);
        onUpdated?.({ ...conversation, left: true });
        return;
      }
      onUpdated?.({
        ...conversation,
        members: members.filter((m) => m.user_id !== userId),
        member_count: Math.max(0, memberCount - 1),
      });
    } catch (err) {
      toastError(err, "Could not update members (team admin required to remove others)");
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Channel members</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {conversation?.title || "Channel"}
          {conversation?.team_name ? ` · ${conversation.team_name}` : ""}
          {` · ${memberCount} member${memberCount === 1 ? "" : "s"}`}
        </p>
        <ul className="max-h-48 overflow-y-auto divide-y border rounded-md">
          {members.length === 0 ? (
            <li className="px-3 py-2 text-xs text-muted-foreground">
              No members loaded yet
            </li>
          ) : (
            members.map((m) => (
              <li
                key={m.user_id}
                className="flex items-center gap-2 px-3 py-2 text-sm"
              >
                <OrgMemberAvatar
                  user={m}
                  className="h-7 w-7"
                  fallbackClassName="text-[10px]"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {getFullName(m)}
                    {m.user_id === currentUserId ? " (you)" : ""}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {m.title || "Member"}
                  </p>
                </div>
                {m.user_id === currentUserId ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyUserId === m.user_id}
                    onClick={() => handleRemove(m.user_id)}
                  >
                    Leave
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyUserId === m.user_id}
                    onClick={() => handleRemove(m.user_id)}
                    title="Team admins can remove others"
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))
          )}
        </ul>
        <div className="space-y-2">
          <p className="text-sm font-medium">Invite people</p>
          {conversation?.is_member === false ? (
            <p className="text-sm text-muted-foreground">
              You must be a channel member to invite others.
            </p>
          ) : (
            <>
              <Input
                value={memberQuery}
                onChange={(e) => setMemberQuery(e.target.value)}
                placeholder="Search org members…"
              />
              <ul className="max-h-36 overflow-y-auto divide-y border rounded-md">
                {candidates.slice(0, 20).map((u) => (
                  <li
                    key={u.user_id}
                    className="flex items-center gap-2 px-3 py-2 text-sm"
                  >
                    <span className="truncate flex-1">{getFullName(u)}</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={inviting}
                      onClick={() => handleInvite(u)}
                    >
                      Invite
                    </Button>
                  </li>
                ))}
                {candidates.length === 0 && (
                  <li className="px-3 py-2 text-xs text-muted-foreground">
                    No matching people to invite
                  </li>
                )}
              </ul>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
